import { ActionContext, Module } from 'vuex'
import { AiCompanyState, RootState, YearEndRecapData } from '../store/state_models'

import { AiWrapperService } from '../services'
import YearEndRecapService from '../services/YearEndRecapService'
import { awaitToJS } from '@/util/await-to-js'
import { company } from './company'
import { user } from './user'
import { CURRENT_ACTIVE_PROMO, PROMO_START_DATE, PROMO_END_DATE, isPromoActive } from '../config/constants'

const RECAP_STORAGE_KEY = 'yearEndRecapData'
const RECAP_MODAL_OPENED_KEY = 'yearEndRecapModalOpened'

export const aiCompanyPlan: Module<AiCompanyState, RootState> = {
  namespaced: true,
  state: {
    isSeasonOfAIEnabled: undefined,
    updatedAt: undefined,
    seasonOfAI: undefined,
    affiliateCode: undefined,
    isLoading: false,
    featureFlags: {
      flags: {
        GlobalSOAIEnabled: false,
        SummerOfAIEnabled: false,
        AIPromosEnabled: isPromoActive(),
        GlobalSOAIClosureDate: PROMO_END_DATE as string | undefined,
        promoStartDate: PROMO_START_DATE as string | undefined,
        promoEndDate: PROMO_END_DATE as string | undefined,
        currentActivePromo: CURRENT_ACTIVE_PROMO as string | undefined,
      },
      companyId: undefined,
    },
    // Year End Recap: undefined = not checked, null = not available, object = available
    recapData: undefined,
    recapLoading: false,
  },
  getters: {
    isSOAEnabled: (state: AiCompanyState) => {
      return state.isSeasonOfAIEnabled
    },
    getSeasonOfAiConfig: (state: AiCompanyState) => {
      return state.seasonOfAI
    },
    getFeatureFlags: (state: AiCompanyState) => {
      return state.featureFlags
    },
    getAffiliateCode: (state: AiCompanyState) => {
      return state.affiliateCode
    },
    isLoading: (state: AiCompanyState) => {
      return state.isLoading
    },
    // Year End Recap getters
    isRecapAvailable: (state: AiCompanyState) => {
      // null = checked but not available, object = available
      return state.recapData !== undefined && state.recapData !== null
    },
    isRecapChecked: (state: AiCompanyState) => {
      // undefined = not checked yet
      return state.recapData !== undefined
    },
    getRecapData: (state: AiCompanyState) => {
      return state.recapData
    },
    isRecapLoading: (state: AiCompanyState) => {
      return state.recapLoading
    },
  },
  mutations: {
    updateSOAEnabled: (state: AiCompanyState, payload: boolean) => {
      state.isSeasonOfAIEnabled = payload
      state.updatedAt = Date.now()
    },
    updateSeasonOfAiConfig: (state: AiCompanyState, payload: object) => {
      state.seasonOfAI = payload
      state.updatedAt = Date.now()
    },
    setSeasonOfAI: (state: AiCompanyState, payload: object) => {
      state.seasonOfAI = payload
    },
    updateFeatureFlags: (
      state: AiCompanyState,
      payload: {
        flags: {
          GlobalSOAIEnabled: boolean
          SummerOfAIEnabled: boolean
          AIPromosEnabled: boolean
          GlobalSOAIClosureDate?: string
          promoStartDate?: string
          promoEndDate?: string
          currentActivePromo?: string
        }
        companyId: string
      }
    ) => {
      state.featureFlags = payload
    },
    updateAffiliateCode: (state: AiCompanyState, payload: string) => {
      state.affiliateCode = payload
    },
    setLoading: (state: AiCompanyState, payload: boolean) => {
      state.isLoading = payload
    },
    // Year End Recap mutations
    setRecapData: (state: AiCompanyState, payload: YearEndRecapData | null) => {
      state.recapData = payload
    },
    setRecapLoading: (state: AiCompanyState, payload: boolean) => {
      state.recapLoading = payload
    },
  },
  actions: {
    /**
     * Get data from store if already fetched, otherwise fetch and return.
     * Prevents duplicate API calls.
     * @param forceRefresh - If true, bypasses cache and fetches fresh data
     */
    getOrFetchData: async (
      context: ActionContext<AiCompanyState, RootState>,
      forceRefresh: boolean = false
    ): Promise<AiCompanyState> => {
      // If already fetched (updatedAt is set) and not forcing refresh, return cached data
      if (context.state.updatedAt && !forceRefresh) {
        return context.state
      }

      // If currently loading, wait for it to complete
      if (context.state.isLoading) {
        await new Promise<void>((resolve) => {
          const check = setInterval(() => {
            if (!context.state.isLoading) {
              clearInterval(check)
              resolve()
            }
          }, 50)
        })
        return context.state
      }

      // Fetch the data
      await context.dispatch('syncAll')
      return context.state
    },

    syncFeatureFlags: async (
      context: ActionContext<AiCompanyState, RootState>,
    ) => {
      const companyId = company?.state?.company.id
      if (!companyId) return
      
      // Calculate dynamic feature flags based on promo config
      const promoActive = isPromoActive()
      
      // Build feature flags object with dynamic values
      const featureFlags = {
        flags: {
          GlobalSOAIEnabled: false, // Always false
          SummerOfAIEnabled: false, // Keep for backward compatibility
          AIPromosEnabled: promoActive, // Based on active promo dates
          GlobalSOAIClosureDate: PROMO_END_DATE, // Promo end date
          promoStartDate: PROMO_START_DATE,
          promoEndDate: PROMO_END_DATE,
          currentActivePromo: CURRENT_ACTIVE_PROMO,
        },
        companyId: companyId,
      }
      
      // Try to fetch from backend (may have additional company-specific flags)
      const [err_feature_flag, feature_flag_response] = await awaitToJS(AiWrapperService.fetchFeatureFlags(companyId))
      
      if (!err_feature_flag && feature_flag_response) {
        // Merge backend response with our dynamic flags (our flags take precedence)
        context.commit('updateFeatureFlags', {
          ...feature_flag_response,
          flags: {
            ...feature_flag_response.flags,
            ...featureFlags.flags, // Our dynamic flags override backend
          },
        })
      } else {
        // Use our calculated flags if backend call fails
        context.commit('updateFeatureFlags', featureFlags)
      }
    },

    syncAll: async (
      context: ActionContext<AiCompanyState, RootState>,
    ) => {
      const companyId = company?.state?.company?.id
      if (!companyId) return

      context.commit('setLoading', true)

      try {
        const [err, response] = await awaitToJS(AiWrapperService.fetchAICompanyPlan(companyId))
        if (err) {
          context.commit('setLoading', false)
          return
        }

        const event = 1735700400000 // '31 December 2024 22:00 EST'
        if(response && response.seasonOfAIOffer) {
          context.commit('setSeasonOfAI', response.seasonOfAIOffer)
        } else {
          context.commit('setSeasonOfAI', {})
        }

        if(response && response.affiliateCode) {
          context.commit('updateAffiliateCode', response.affiliateCode)
        }

        if(response 
          && (('seasonOfAIDisabled' in response && response.seasonOfAIDisabled === false) || !('seasonOfAIDisabled' in response))
          && Date.now() <= event) {
          context.commit('updateSOAEnabled', true)
        } else {
          context.commit('updateSOAEnabled', false)
        }

        context.commit('setLoading', false)

      } catch {
        context.commit('updateSOAEnabled', false)
        context.commit('setLoading', false)
      }
    },

    // ============ Year End Recap Actions ============

    /**
     * Get recap data from store if already checked, otherwise fetch.
     * - undefined = not checked yet
     * - null = checked but not available  
     * - object = available
     */
    getOrFetchRecapData: async (
      context: ActionContext<AiCompanyState, RootState>,
      forceRefresh: boolean = false
    ): Promise<YearEndRecapData | null | undefined> => {
      // Only agency users can see recap
      const currentUser = user?.state?.user
      if (currentUser?.type !== 'agency') {
        context.commit('setRecapData', null)
        return null
      }

      const companyId = company?.state?.company?.id
      if (!companyId) return undefined

      // If already checked and not forcing refresh, return cached data
      if (context.state.recapData !== undefined && !forceRefresh) {
        return context.state.recapData
      }

      // If currently loading, wait for it
      if (context.state.recapLoading) {
        await new Promise<void>((resolve) => {
          const check = setInterval(() => {
            if (!context.state.recapLoading) {
              clearInterval(check)
              resolve()
            }
          }, 50)
        })
        return context.state.recapData
      }

      // Try localStorage first (for session persistence)
      if (!forceRefresh) {
        try {
          const cached = localStorage.getItem(RECAP_STORAGE_KEY)
          if (cached) {
            const parsed = JSON.parse(cached)
            if (parsed.companyId === companyId) {
              context.commit('setRecapData', parsed.data)
              return parsed.data
            }
          }
        } catch (e) {
          // Ignore localStorage errors
        }
      }

      // Fetch from API
      context.commit('setRecapLoading', true)
      try {
        const { isAvailable, data } = await YearEndRecapService.checkRecapAvailability(companyId)
        
        const recapData = isAvailable && data ? data : null
        context.commit('setRecapData', recapData)

        // Cache to localStorage
        try {
          localStorage.setItem(RECAP_STORAGE_KEY, JSON.stringify({
            companyId,
            data: recapData,
            cachedAt: Date.now(),
          }))
        } catch (e) {
          // Ignore localStorage errors
        }

        return recapData
      } catch (error) {
        console.error('Failed to fetch year-end recap:', error)
        context.commit('setRecapData', null)
        return null
      } finally {
        context.commit('setRecapLoading', false)
      }
    },

    /**
     * Check if recap modal has been opened before (from localStorage)
     */
    hasRecapModalOpened: (): boolean => {
      try {
        return localStorage.getItem(RECAP_MODAL_OPENED_KEY) === 'true'
      } catch {
        return false
      }
    },

    /**
     * Mark recap modal as opened
     */
    markRecapModalOpened: () => {
      try {
        localStorage.setItem(RECAP_MODAL_OPENED_KEY, 'true')
      } catch {
        // Ignore
      }
    },

    /**
     * Check and auto-open Year End Recap modal if conditions are met.
     * Call this once on app load - it handles everything.
     * 
     * @param payload.emitter - Event emitter to emit the modal open event
     * @param payload.route - Current route object
     * @param payload.openEventName - Event name to emit (default: 'load-year-end-recap-modal')
     */
    checkAndAutoOpenRecapModal: async (
      context: ActionContext<AiCompanyState, RootState>,
      payload: { 
        emitter: any
        route: any
        openEventName?: string 
      }
    ): Promise<boolean> => {
      try {
        const { emitter, route, openEventName = 'load-year-end-recap-modal' } = payload

        // Fetch recap data (uses cache if available)
        const recapData = await context.dispatch('getOrFetchRecapData')
        
        if (!recapData) {
          console.warn('Year End Recap not available for this company')
          return false
        }        
        // Check if modal has already been opened
        const hasOpened = await context.dispatch('hasRecapModalOpened')
        if (hasOpened) {
          return false
        }
        
        // Don't auto-open if on a location-specific page
        if (route?.params?.location_id) {
          return false
        }
        
        // Auto-open the modal with a small delay
        setTimeout(() => {
          emitter.emit(openEventName, { 
            source: 'auto-open', 
            url: route?.path, 
            payload: {
              companyId: company?.state?.company?.id,
              pageName: route?.name || 'Agency Dashboard',
              pageUrl: window.location.href,
              page: route?.path,
              timestamp: new Date().toISOString()
            } 
          })
        }, 5000)
        
        return true
      } catch (e) {
        console.warn('Failed to check/open Year End Recap modal:', e)
        return false
      }
    },

    // ============ Original Actions ============

    updateSOAEnabled: (context: ActionContext<AiCompanyState, RootState>, payload: boolean) => {
      context.commit('updateSOAEnabled', payload)
    },
    updateSeasonOfAiConfig: (context: ActionContext<AiCompanyState, RootState>, payload: object) => {
      context.commit('setSeasonOfAI', payload)
    },
    updateFeatureFlags: (
      context: ActionContext<AiCompanyState, RootState>,
      payload: {
        flags: {
          GlobalSOAIEnabled: boolean
          SummerOfAIEnabled: boolean
          AIPromosEnabled: boolean
          GlobalSOAIClosureDate?: string
          promoStartDate?: string
          promoEndDate?: string
          currentActivePromo?: string
        }
        companyId: string
      }
    ) => {
      context.commit('updateFeatureFlags', payload)
    }
  },
}
